import * as THREE from 'three';

/**
 * Where a loaded model sits on the turntable.
 *
 * Placement is computed in the model's LOCAL space on purpose. The obvious
 * alternative, Box3.setFromObject(), returns WORLD bounds, which already bake in
 * the wrapper group's rotation; subtracting that centre from a child's local
 * position double-counts the rotation, so the car drifts off centre the moment
 * the user orbits. Here the caller passes local bounds and applies the rotation
 * afterwards, so the result cannot depend on it.
 */

export interface ModelPlacementInput {
  /** Bounds of the loaded scene in its own local space, before any wrapper rotation. */
  localBounds: THREE.Box3;
  scale: number;
  rotationY: number;
  offsetY: number;
  /** Height of the turntable surface. */
  groundY?: number;
}

export interface ModelPlacement {
  position: [number, number, number];
  rotationY: number;
  scale: number;
  /** Largest half-extent on X/Z after scaling, for the turntable and camera fit. */
  footprintRadius: number;
}

export function computeModelPlacement(input: ModelPlacementInput): ModelPlacement {
  const { localBounds, scale, rotationY, offsetY } = input;
  const groundY = input.groundY ?? 0;

  const size = localBounds.getSize(new THREE.Vector3());
  const center = localBounds.getCenter(new THREE.Vector3());

  // Using the max half-extent keeps the car inside the turntable radius for any
  // orientation, including the truck's 90-degree wrapper rotation.
  const footprintRadius = Math.max((size.x * scale) / 2, (size.z * scale) / 2);

  return {
    position: [
      -center.x * scale,
      -localBounds.min.y * scale + groundY + offsetY,
      -center.z * scale,
    ],
    rotationY,
    scale,
    footprintRadius,
  };
}

/**
 * Union of each child's geometry bounds translated by its own local position.
 *
 * This is the local-space equivalent of setFromObject() and is what keeps
 * placement independent of the wrapper's rotation.
 */
export function computeLocalBounds(root: THREE.Object3D): THREE.Box3 {
  const localBounds = new THREE.Box3();
  const childBounds = new THREE.Box3();

  root.updateMatrixWorld(true);
  // matrixWorld is relative to the scene, not to root, and these models are
  // deeply nested (GT500.Rig > DEF-Body > ...). Converting into root space first
  // is what makes the union correct for a rigged hierarchy.
  const rootInverse = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const rootRelative = new THREE.Matrix4();

  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.geometry) return;
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
    if (!mesh.geometry.boundingBox) return;
    rootRelative.multiplyMatrices(rootInverse, mesh.matrixWorld);
    childBounds.copy(mesh.geometry.boundingBox).applyMatrix4(rootRelative);
    localBounds.union(childBounds);
  });

  return localBounds;
}